import type { ReactNode } from "react";

/** Dot badges rely on color alone, so they must expose an accessible label. */
export type BadgeVariant = "neutral" | "emphasis" | "brand" | "info" | "success" | "warning" | "danger";
export type BadgeSize = "default" | "compact";

const SIZE_CLASSES: Record<BadgeSize, string> = {
  default: "h-6 rounded-full px-2",
  compact: "h-5 rounded px-1.5",
};

const VARIANT_CLASSES: Record<BadgeVariant, string> = {
  neutral: "bg-surface-base text-content-tertiary border border-border-subtle",
  emphasis: "bg-surface-secondary text-content-primary",
  brand: "bg-brand-primary-subtle text-brand-primary-on-subtle",
  info: "bg-info-subtle text-info-on-subtle",
  success: "bg-success-subtle text-success-on-subtle",
  warning: "bg-warning-subtle text-warning-on-subtle",
  danger: "bg-error-subtle text-error-on-subtle",
};

const DOT_CLASSES: Record<BadgeVariant, string> = {
  neutral: "bg-content-tertiary",
  emphasis: "bg-content-primary",
  brand: "bg-brand-primary",
  info: "bg-info",
  success: "bg-success",
  warning: "bg-warning",
  danger: "bg-error",
};

interface BadgeSharedProps {
  variant?: BadgeVariant;
  size?: BadgeSize;
}

export interface BadgeDotProps extends BadgeSharedProps {
  dot: true;

  "aria-label": string;
}

export interface BadgePillProps extends BadgeSharedProps {
  dot?: false;

  children?: ReactNode;

  icon?: ReactNode;
  trailingIcon?: ReactNode;
  "aria-label"?: string;
}

export type BadgeProps = BadgeDotProps | BadgePillProps;

export function Badge(props: Readonly<BadgeProps>) {
  const { variant = "neutral", size = "default" } = props;

  if (props.dot) {
    return (
      <span
        role="img"
        aria-label={props["aria-label"]}
        className={["inline-block size-2 shrink-0 rounded-full", DOT_CLASSES[variant]].join(" ")}
      />
    );
  }

  return (
    <span
      className={[
        "inline-flex items-center gap-1 text-xs font-medium",
        SIZE_CLASSES[size],
        VARIANT_CLASSES[variant],
      ].join(" ")}
    >
      {props.icon}
      <span className="pt-px leading-none">{props.children}</span>
      {props.trailingIcon}
    </span>
  );
}
